import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { loginUser, signupUser, logoutUser } from "src/apis/auth";
import { switchOrganizationApi } from "src/apis/organizations";
import { setSentryUser } from "src/sentry";

const AuthContext = createContext(null);

function updateCsrfMeta(token) {
  if (!token) return;
  const meta = document.querySelector('meta[name="csrf-token"]');
  if (meta) meta.setAttribute("content", token);
}

export function AuthProvider({ children, initialData = {} }) {
  const [currentUser, setCurrentUser] = useState(initialData.user || null);
  const [currentMembership, setCurrentMembership] = useState(initialData.current_membership || null);
  const [organization, setOrganization] = useState(initialData.organization || null);
  const [organizations, setOrganizations] = useState(initialData.organizations || []);
  // Non-null only during an admin support-login session. Drives the banner.
  const [impersonation] = useState(initialData.impersonation || null);
  const navigate = useNavigate();

  // Keep Sentry's user context in sync with auth state so reported errors are
  // attributed to the signed-in user (and cleared on logout).
  useEffect(() => {
    setSentryUser(currentUser);
  }, [currentUser]);

  const login = useCallback(
    async ({ email, password, rememberMe }) => {
      const { data } = await loginUser({ email, password, rememberMe });
      updateCsrfMeta(data.csrf_token);
      setCurrentUser(data.user);
      setCurrentMembership(data.current_membership);
      setOrganization(data.organization);
      setOrganizations(data.organizations || []);
      // Customers (buyers) get the read-only portal; staff get the CRM.
      const isCustomerLogin = data.current_membership?.role?.slug === "customer";
      navigate(isCustomerLogin ? "/portal" : "/dashboard", { replace: true });
      return data;
    },
    [navigate]
  );

  const signup = useCallback(
    async (values) => {
      const { data } = await signupUser(values);
      updateCsrfMeta(data.csrf_token);
      setCurrentUser(data.user);
      setCurrentMembership(data.current_membership);
      setOrganization(data.organization);
      setOrganizations(data.organizations || []);
      navigate("/dashboard", { replace: true });
      return data;
    },
    [navigate]
  );

  const logout = useCallback(async () => {
    try {
      const { data } = await logoutUser();
      // Rails resets the session on sign-out and issues a new CSRF token.
      // We must update the meta tag now, before the next sign-in request,
      // otherwise the stale token causes a 422.
      updateCsrfMeta(data?.csrf_token);
    } catch {
      // Even if the request fails (network error, already logged out),
      // clear local state and redirect — don't leave the user stuck.
    }
    setCurrentUser(null);
    setCurrentMembership(null);
    setOrganization(null);
    setOrganizations([]);
    navigate("/login", { replace: true });
  }, [navigate]);

  const switchOrganization = useCallback(
    async (organizationId) => {
      const { data } = await switchOrganizationApi(organizationId);
      updateCsrfMeta(data.csrf_token);
      setOrganization(data.organization);
      setCurrentMembership(data.current_membership);
      setCurrentUser((prev) => ({ ...prev, role: data.current_membership?.role?.slug }));
      return data;
    },
    []
  );

  const can = useCallback(
    (resource, action) => {
      if (!currentMembership?.permissions) return false;
      const actions = currentMembership.permissions[resource];
      return Array.isArray(actions) && actions.includes(action);
    },
    [currentMembership]
  );

  // Callers are restricted to partial phone numbers (backend masks them) and
  // must not be able to copy or bulk-export numbers — the UI reads this to hide
  // export controls and block copy on phone fields.
  const isCaller = currentMembership?.role?.slug === "caller";

  // Customers (buyers) live in the read-only /portal, never the staff CRM.
  // Routes use this to keep the two worlds separate.
  const isCustomer = currentMembership?.role?.slug === "customer";

  const value = useMemo(
    () => ({
      currentUser,
      setCurrentUser,
      currentMembership,
      setCurrentMembership,
      organization,
      setOrganization,
      organizations,
      setOrganizations,
      impersonation,
      isAuthenticated: !!currentUser,
      login,
      signup,
      logout,
      switchOrganization,
      can,
      isCaller,
      isCustomer,
    }),
    [currentUser, currentMembership, organization, organizations, impersonation, login, signup, logout, switchOrganization, can, isCaller, isCustomer]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
